import {
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";

/**
 * Number of pages shown on each side of the current page in the middle
 * window. All other window/threshold values are derived from this constant
 * so the pagination math has a single source of truth.
 */
const SIBLING_COUNT = 2;

// Pages always rendered: first + last + (current ± SIBLING_COUNT).
const MIDDLE_WINDOW_SIZE = SIBLING_COUNT * 2 + 1;

/**
 * Builds a list of pagination link elements for the shared Paginator component.
 *
 * Renders continuous page numbers for single hidden-page gaps and collapses only
 * larger hidden ranges behind ellipsis separators.
 *
 * @param {number} currentPage - The currently active page (1-based).
 * @param {number} totalPages - Total number of pages available.
 * @param {Function} onPageChange - Called when a page link is clicked.
 * @returns {React.JSX.Element[]} Ordered list of pagination link and ellipsis elements.
 */
export const generatePaginationLinks = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
): React.JSX.Element[] => {
  const pages: React.JSX.Element[] = [];

  const createPageItem = (page: number): React.JSX.Element => (
    <PaginationItem key={`page-${page}`}>
      <PaginationLink
        onClick={() => onPageChange(page)}
        isActive={page === currentPage}
        disabled={page === currentPage}
      >
        {page}
      </PaginationLink>
    </PaginationItem>
  );

  const createEllipsisItem = (key: string): React.JSX.Element => (
    <PaginationItem key={key}>
      <PaginationEllipsis />
    </PaginationItem>
  );

  if (totalPages <= MIDDLE_WINDOW_SIZE) {
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      pages.push(createPageItem(pageNumber));
    }

    return pages;
  }

  let startPage = Math.max(2, currentPage - SIBLING_COUNT);
  let endPage = Math.min(totalPages - 1, currentPage + SIBLING_COUNT);

  if (currentPage <= SIBLING_COUNT + 1) {
    endPage = SIBLING_COUNT * 2;
  } else if (currentPage >= totalPages - SIBLING_COUNT) {
    startPage = totalPages - SIBLING_COUNT * 2 + 1;
  }

  pages.push(createPageItem(1));

  if (startPage > 2) {
    if (startPage === 3) {
      pages.push(createPageItem(2));
    } else {
      pages.push(createEllipsisItem("left-ellipsis"));
    }
  }

  for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
    pages.push(createPageItem(pageNumber));
  }

  if (endPage < totalPages - 1) {
    if (endPage === totalPages - 2) {
      pages.push(createPageItem(totalPages - 1));
    } else {
      pages.push(createEllipsisItem("right-ellipsis"));
    }
  }

  pages.push(createPageItem(totalPages));

  return pages;
};
